import { useCallback, useContext } from 'react';
import debounce from 'lodash.debounce';

import TaskDateField from '@atom/components/common/workOrderDetail/taskFields/TaskDateField';
import TaskEnumMultipleField from '@atom/components/common/workOrderDetail/taskFields/TaskEnumMultipleField';
import TaskEnumMultipleFieldList from '@atom/components/common/workOrderDetail/taskFields/TaskEnumMultipleFieldList';
import TaskEnumSingleField from '@atom/components/common/workOrderDetail/taskFields/TaskEnumSingleField';
import TaskEnumSingleFieldList from '@atom/components/common/workOrderDetail/taskFields/TaskEnumSingleFieldList';
import TaskHyperlinkField from '@atom/components/common/workOrderDetail/taskFields/TaskHyperlinkField';
import TaskNumericField from '@atom/components/common/workOrderDetail/taskFields/TaskNumericField';
import TaskTextField from '@atom/components/common/workOrderDetail/taskFields/TaskTextField';
import {
  DataType,
  FieldViewType,
  TaskFieldDataType,
} from '@atom/types/dataType';
import { Task, TASK_FIELD_DATA_TYPES, TaskField } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  CustomFieldView,
  updateFieldInTask,
} from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import { isWorkOrderCompletedOrAbove } from '@atom/utilities/workOrderStatusUtilities';

import BulkContext from '../../../BulkContext';
import { useWorkOrderValidation } from '../../../hooks/bulkWorkOrderHook';
import Field from '../Field';

import classes from './task.module.css';

const DEBOUNCE_TIME = 1000;

export interface Props {
  taskIndex: number;
}

const TaskFields = ({ taskIndex }: Props) => {
  const { invalidFields, workOrder, setWorkOrder } = useContext(BulkContext);
  const { validateTaskField } = useWorkOrderValidation();
  const { tasks } = workOrder;
  const task = tasks[taskIndex];
  const { fieldOrder, fields } = task;

  const handleChange = (fieldId: string, value: any) => {
    // Update field's value in task and set it on the work order:
    const updatedTask = updateFieldInTask(task, fieldId, value);
    const newWorkOrder = {
      ...workOrder,
      tasks: tasks.map((taskItem: Task, indexValue: number) =>
        indexValue === taskIndex ? { ...updatedTask } : taskItem,
      ),
    };
    setWorkOrder(newWorkOrder);

    // And run validation:
    validateTaskField(fieldId, updatedTask);
  };

  const handleChangeDebounced = useCallback(
    debounce(handleChange, DEBOUNCE_TIME),
    [handleChange],
  );

  const isRequiredOnCompleteStatus = isWorkOrderCompletedOrAbove(
    workOrder.statusId,
  );

  const getTaskFieldIcon = (dataType: TaskFieldDataType) => {
    const option = TASK_FIELD_DATA_TYPES.find(
      item => item.dataType === dataType,
    );

    return option?.icon;
  };

  const getContent = (field: TaskField) => {
    const isFieldEditDisabled = doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);
    const invalidFieldIds = invalidFields[task.id] || new Set([]);
    const isError = invalidFieldIds.has(field.id);

    const props = {
      // Task fields are only required once the work order is being created
      // directly into a Completed/Closed status, so only label them then.
      field: {
        ...field,
        required: field.required && isRequiredOnCompleteStatus,
      },
      error: isError,
      helperText: isError ? 'This field is required' : null,
      onChange: handleChange,
      isDisabled: isFieldEditDisabled,
      previewView: true,
      onBlur: handleChange,
      customFieldView: CustomFieldView.DETAIL,
    };

    const hasSubFields = !isNilOrEmpty(field.subFields);

    const isListView = field.viewAs === FieldViewType.CHECKBOX;

    const components = {
      [DataType.SHORT_TEXT]: (
        <TaskTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.LONG_TEXT]: (
        <TaskTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE]: (
        <TaskDateField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE_TIME]: (
        <TaskDateField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_SINGLE]: hasSubFields ? (
        <div className={classes.enumSingleNestedContainer}>
          <TaskEnumSingleField
            {...props}
            onChange={handleChangeDebounced}
            invalidFieldIds={invalidFieldIds}
          />
        </div>
      ) : isListView ? (
        <TaskEnumSingleFieldList
          {...props}
          invalidFieldIds={invalidFieldIds}
          fieldsLoading={new Set([])}
        />
      ) : (
        <TaskEnumSingleField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_MULTIPLE]: isListView ? (
        <TaskEnumMultipleFieldList {...props} />
      ) : (
        <TaskEnumMultipleField {...props} />
      ),
      [DataType.NUMBER]: (
        <TaskNumericField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.HYPERLINK]: <TaskHyperlinkField {...props} />,
    };

    return (
      <Field
        icon={getTaskFieldIcon(field?.dataType)}
        input={components[field.dataType]}
      />
    );
  };

  return (
    <>
      {fieldOrder.map((fieldId: string) => {
        const field = fields.find(item => item.id === fieldId);

        if (!field) {
          return null;
        }

        return getContent(field);
      })}
    </>
  );
};

export default TaskFields;
